import { Component, Input, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { StatusCardConfig } from '@shared/components/widgets/status-card/status-card-config';
import { NgTemplateOutlet } from '@angular/common';
import { catchError, of } from 'rxjs';
import { ArrowUpRight, CircleAlert, TriangleAlert, LucideAngularModule } from 'lucide-angular';
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';

@Component({
  selector: 'app-status-card',
  imports: [
    TranslatePipe,
    RouterLink,
    NgTemplateOutlet,
    LucideAngularModule,
    TooltipDirective,
  ],
  templateUrl: './status-card.component.html',
  styleUrl: './status-card.component.css'
})
export class StatusCardComponent implements OnInit {
  @Input() public config!: StatusCardConfig;

  public value?: string | number;
  public loading: boolean = true;
  public hasError: boolean = false;

  protected readonly CircleAlert = CircleAlert;
  protected readonly ArrowUpRight = ArrowUpRight;
  protected readonly TriangleAlert = TriangleAlert;

  public ngOnInit(): void {
    if (this.config.value !== undefined) {
      this.value = this.config.value;
      this.loading = false;
      return;
    }

    if (this.config.serviceCallback) {
      this.config.serviceCallback().pipe(
        catchError(() => {
          this.hasError = true;
          this.loading = false;
          return of(null);
        })
      ).subscribe((data) => {
        if (data === null) return;

        if (this.config.statusCodes) {
          const values = Object.values(data);
          let code: string | undefined;
          if (values.length === 1) {
            code = String(values[0]).toLowerCase();
          }
          this.value = code ? this.config.statusCodes[code] : 'bg-slate-300';
        } else {
          this.value = Array.isArray(data) ? data.length : (data.totalHitCount ?? data.count);
        }

        this.loading = false;
      });
    } else {
      this.loading = false;
    }
  }

  public get numericValue(): number {
    return typeof this.value === 'number' ? this.value : 0;
  }

  public get isOverLimit(): boolean {
    return this.config.limit !== undefined && this.numericValue > this.config.limit;
  }

  /** e.g. "+42%" when value is 42% above the limit */
  public get overLimitPercent(): string {
    if (!this.isOverLimit || !this.config.limit) return '';
    const pct = Math.round(((this.numericValue - this.config.limit) / this.config.limit) * 100);
    return `+${pct}%`;
  }

  /** Color of the bottom accent stripe */
  public get accentClass(): string {
    if (this.loading) return 'bg-gray-100 dark:bg-dark-border animate-pulse';
    if (this.hasError) return 'bg-red-400';
    if (this.config.statusCodes) return this.statusCodeAccent;
    if (this.isOverLimit) return 'bg-amber-500';
    return this.numericValue === 0 ? 'bg-emerald-400' : 'bg-violet-500';
  }

  private get statusCodeAccent(): string {
    if (typeof this.value === 'string') {
      if (this.value.includes('green')) return 'bg-emerald-500';
      if (this.value.includes('red')) return 'bg-red-500';
      if (this.value.includes('yellow')) return 'bg-yellow-400';
    }
    return 'bg-slate-300';
  }

  /** Color of the large numeric value — always neutral, accent bar carries the state colour */
  public get numberClass(): string {
    return 'text-gray-800 dark:text-white';
  }

}
